import { useState } from 'react';
import {
    RequestStatus,
    useServerErrorEffect,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks';
import {
    PayTicketButton,
    TicketPlusApprovalRequired,
} from '../../common/components/ticketplus/TicketPlusApprovalRequired';
import { TicketDisplay } from '../../common/components/ticketplus/TicketPlusLayout';
import { PaymentStateModal } from './QuickCheckoutModals';
import {
    FlowStateAliasPending,
    FlowStateTicketScanned,
    PaymentModalState,
    StartPaymentResponse,
} from './QuickCheckoutTypes';
import { useParams, useNavigate } from 'react-router';
import { TicketPlusClaimedModal } from '../../common/components/ticketplus/TicketPlusClaimed';
import { TicketPlusTooManyApprovedTicketsModal } from '../../common/components/ticketplus/TicketPlusTooManyApprovedTickets';
import { QuickCheckoutTosCheckbox } from '../common/QuickCheckoutTosCheckbox';
import { QuickCheckoutFooter } from '../common/QuickCheckoutLayout';
import { PaymentMethodEnum } from '../../api/Http.ts';
import {
    FormFieldValidationError,
    QuickCheckoutInput,
    QuickCheckoutInputLabel,
} from '../common/QuickCheckoutUiComponents.tsx';
import { Localized, useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { css } from '@emotion/css';
import { get, useForm } from 'react-hook-form';
import { makeLocalizedText } from 'dg-web-shared/lib/Localized.ts';
import * as emailValidator from 'email-validator';

export const QuickCheckoutFlowTicketScanned = (
    props: (FlowStateTicketScanned | FlowStateAliasPending) & {
        paymentModalState?: PaymentModalState;
        onPaymentStateModalConfirm: () => void;
        refetchFlowState: () => void;
    },
) => {
    const { ticketId } = useParams();
    const { language } = useLanguage();

    const [openTicketAlreadyClaimedModal, setOpenTicketAlreadyClaimedModal] =
        useState(false);
    const [openTooManyApprovedTicketsModal, setTooManyApprovedTicketsModal] =
        useState(false);
    const [paymentStartState, writePaymentStartState] = useServerWrite<
        {
            ticketId: string;
            baseUri: string;
            tosAndPrivacyPolicyAccepted: boolean;
            email: string | null;
        },
        StartPaymentResponse,
        StartPaymentErrorResponse
    >(() => ({
        url: `/ui-api/quickcheckout/payment/start`,
    }));

    useServerSuccessEffect(paymentStartState, response => {
        window.location.href = response.redirectUrl;
    });

    useServerErrorEffect(paymentStartState, (_, res) => {
        switch (res?.errorType) {
            case StartPaymentErrorType.TICKET_ALREADY_CLAIMED:
                setOpenTicketAlreadyClaimedModal(true);
                break;
            case StartPaymentErrorType.TOO_MANY_APPROVED_TICKETS:
                setTooManyApprovedTicketsModal(true);
                break;
            default:
                props.refetchFlowState();
                break;
        }
    });

    const [tosAndPrivacyCheckboxSelected, setTosAndPrivacyCheckbox] = useState(
        props.flowState === 'TICKET_SCANNED' &&
            props.tosAndPrivacyPolicyAccepted,
    );

    const form = useForm<EmailFormValues>({
        defaultValues: { [EmailFormFields.email]: '' },
    });

    const navigate = useNavigate();

    return (
        <>
            {props.paymentModalState !== undefined && (
                <PaymentStateModal
                    state={props.paymentModalState}
                    onConfirm={props.onPaymentStateModalConfirm}
                />
            )}
            <TicketPlusApprovalRequired
                onOpenZoneInfo={() => navigate('parking-details')}
                zoneName={props.zoneInfo.name}
                ticketCreatedAt={props.ticketWithState.createdAt}
                optionalField={
                    <>
                        <QuickCheckoutInputLabel
                            htmlFor={EmailFormFields.email}
                        >
                            <Localized
                                de="E-Mail (optional, für den Versand der Quittung)"
                                fr="E-mail (facultatif, pour l'envoi du reçu)"
                                it="E-mail (facoltativo, per l'invio della ricevuta)"
                                en="E-mail (optional, for the receipt delivery)"
                            />
                        </QuickCheckoutInputLabel>
                        <QuickCheckoutInput
                            type="email"
                            placeholder="E-mail"
                            className={css({
                                width: '100%',
                            })}
                            {...form.register(EmailFormFields.email, {
                                validate: email => {
                                    if (!email) {
                                        return true;
                                    } else if (
                                        !emailValidator.validate(email)
                                    ) {
                                        return makeLocalizedText(language)({
                                            de: 'Ungültige E-Mail',
                                            fr: 'Adresse e-mail invalide',
                                            it: 'Indirizzo e-mail non valido',
                                            en: 'Invalid e-mail',
                                        });
                                    } else {
                                        return true;
                                    }
                                },
                            })}
                        />
                        <FormFieldValidationError
                            errors={form.formState.errors}
                            name={EmailFormFields.email}
                        />
                    </>
                }
            />
            {openTicketAlreadyClaimedModal && (
                <TicketPlusClaimedModal
                    onClose={() => setOpenTicketAlreadyClaimedModal(false)}
                />
            )}
            {openTooManyApprovedTicketsModal && (
                <TicketPlusTooManyApprovedTicketsModal
                    onClose={() => setTooManyApprovedTicketsModal(false)}
                    activePaymentType={PaymentMethodEnum.DIRECT}
                />
            )}
            <QuickCheckoutFooter>
                {props.flowState === 'TICKET_SCANNED' &&
                    !props.tosAndPrivacyPolicyAccepted && (
                        <QuickCheckoutTosCheckbox
                            selected={tosAndPrivacyCheckboxSelected}
                            setSelected={setTosAndPrivacyCheckbox}
                        />
                    )}
                <TicketDisplay ticket={props.ticketWithState.ticket} />
                <PayTicketButton
                    loading={
                        paymentStartState.status === RequestStatus.PENDING ||
                        paymentStartState.status === RequestStatus.SUCCESS
                    }
                    disabled={
                        (props.flowState === 'TICKET_SCANNED' &&
                            !tosAndPrivacyCheckboxSelected) ||
                        Boolean(
                            get(form.formState.errors, EmailFormFields.email),
                        )
                    }
                    onClick={form.handleSubmit(form => {
                        writePaymentStartState({
                            ticketId: ticketId ?? '',
                            baseUri: window.location.toString(),
                            tosAndPrivacyPolicyAccepted:
                                tosAndPrivacyCheckboxSelected,
                            email: form.email ? form.email : null,
                        });
                    })}
                />
            </QuickCheckoutFooter>
        </>
    );
};

interface StartPaymentErrorResponse {
    errorType: StartPaymentErrorType;
}

enum StartPaymentErrorType {
    TICKET_ALREADY_CLAIMED = 'TICKET_ALREADY_CLAIMED',
    TOO_MANY_APPROVED_TICKETS = 'TOO_MANY_APPROVED_TICKETS',
}

type EmailFormValues = { [EmailFormFields.email]: string };

const enum EmailFormFields {
    email = 'email',
}
